import { Injectable } from "@angular/core";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { IScratchPadDataLookupService } from "../IScratchPadDataLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { COLUMN_DESK_ID } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";

@Injectable()
export class ScratchPadDataLookupService
  extends BaseLookupService<string, CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl>
  implements IScratchPadDataLookupService
{
  constructor(protected traderGroupLookup: TraderGroupLookupService) {
    super(CubiqTS.LinkedOrderRequestImpl);
  }

  getItemKey(item: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl): string {
    let order = item as CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl;
    if (order instanceof CubiqTS.LinkedOrderRequestImpl) {
      return order.rootOrder.externalClientReference;
    } else if (order instanceof CubiqTS.FixingOrderRequestImpl) {
      return order.fixingOrderData.externalClientReference;
    }
  }

  public deleteItemByReference(extReference: string): void {
    super.deleteItemById(extReference);
  }

  public getItemByReference(
    extReference: string,
  ): CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl {
    return this.getItem(extReference);
  }

  mapItem(item: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl): void {
    super.mapItem(item);

    let orderKey: string;
    try {
      orderKey = this.getItemKey(item);
      let traderGroupId: CubiqTS.Long = this.getTraderGroupId(orderKey);
      if (traderGroupId != null) {
        item.extendedInfo.set(COLUMN_DESK_ID, traderGroupId.toString());
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to determine trader group for item ${orderKey}: ${e}`,
      );
    }
  }

  public getTraderGroupId(extReference: string): CubiqTS.Long {
    let request: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl =
      this.getItem(extReference);

    let traderGroupName: string = null;

    if (
      request != null &&
      request instanceof CubiqTS.LinkedOrderRequestImpl &&
      request.rootOrder != null &&
      request.rootOrder.traderGroup != null
    ) {
      traderGroupName = request.rootOrder.traderGroup;
    } else if (request != null && request instanceof CubiqTS.FixingOrderRequestImpl) {
      let fixingRequest: CubiqTS.FixingOrderRequestImpl = request as CubiqTS.FixingOrderRequestImpl;
      if (
        fixingRequest.fixingOrderData != null &&
        fixingRequest.fixingOrderData.traderGroup != null
      ) {
        traderGroupName = fixingRequest.fixingOrderData.traderGroup;
      }
    }

    if (traderGroupName != null) {
      let traderGroup: CubiqTS.TradingGroupImpl =
        this.traderGroupLookup.getTraderGroupByName(traderGroupName);
      if (traderGroup != null) {
        return traderGroup.groupId;
      }
    }

    return null;
  }

  /**
   * Populate trading channel for a new order request by checking
   * if there is an existing scratchpad order with the same external reference
   * that already has the trading channel populated and that using
   * that trading channel
   * @param request the request to be checked
   */
  public processTradingChannel(
    request:
      | CubiqTS.LinkedOrderRequestImpl
      | CubiqTS.LinkedOrderModificationRequestImpl
      | CubiqTS.FixingOrderRequestImpl
      | CubiqTS.FixingOrderModificationRequestImpl,
  ) {
    try {
      // No modifications need to be processed, just new requests
      if (request instanceof CubiqTS.LinkedOrderRequestImpl) {
        let externalClientReference = this.getItemKey(request);
        if (externalClientReference != null) {
          let order = this.getItemByReference(externalClientReference);
          if (
            order instanceof CubiqTS.LinkedOrderRequestImpl &&
            order.rootOrder?.tradingChannel != null
          ) {
            // Now populate the rest of the strategy with the same channel
            this.populateTradingChannel(request.rootOrder, order.rootOrder.tradingChannel);
          }
        }
      } else if (request instanceof CubiqTS.FixingOrderRequestImpl) {
        let externalClientReference = this.getItemKey(request);
        if (externalClientReference != null) {
          let order = this.getItemByReference(externalClientReference);
          if (
            order instanceof CubiqTS.FixingOrderRequestImpl &&
            order.fixingOrderData?.tradingChannel != null
          ) {
            request.fixingOrderData.tradingChannel = order.fixingOrderData.tradingChannel;
          }
        }
      }
    } catch (err) {
      logger.out.error(`Failed to replace trading channel on order request.`, err);
    }
  }

  /**
   * Recursivelly populate the trading channel of the strategy
   * @param order the order to populate the trading channel and inspect for sibling and child orders
   * @param channel the channel to use
   * @param fromSibling if the call comes from a sibling
   */
  private populateTradingChannel(
    order: CubiqTS.OrderRequestDataImpl,
    channel: string,
    fromSibling: boolean = false,
  ): void {
    order.tradingChannel = channel;

    if (fromSibling == false && order.sibling?.order != null) {
      this.populateTradingChannel(order.sibling.order, channel, true);
    }

    if (Array.isArray(order.childRelationships)) {
      order.childRelationships
        .filter((r) => r.order != null)
        .forEach((r) => this.populateTradingChannel(r.order, channel));
    }
  }
}
